import { Box, Table, TableHead, TableBody, TableRow, TableCell, Typography, Tooltip, makeStyles } from '@material-ui/core';
import GridIcon from '@material-ui/icons/GridOn';
import StatSection from './StatSection';
import useTableRules from '../../UI/useTableRules';
import { HEAT_STEPS, HEAT_TEXT, heatStep, heatFloor } from './palette';

// The cells of the board, in the order the scoreboard lays them out
const CELL_NAMES = [
    'Top left', 'Top', 'Top right',
    'Left', 'Centre', 'Right',
    'Bottom left', 'Bottom', 'Bottom right'
];

// A corner sits on three lines, a side on two and the centre on four, so which
// of them a contestant takes says something about how they played
const CELL_GROUPS = [
    { name: 'Corners', cells: [0, 2, 6, 8] },
    { name: 'Sides', cells: [1, 3, 5, 7] },
    { name: 'Centre', cells: [4] }
];

const useStyles = makeStyles(theme => ({
    board: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 4,
        maxWidth: 260,
        margin: '0 auto'
    },
    cell: {
        position: 'relative',
        paddingTop: '100%',
        borderRadius: 4
    },
    empty: {
        backgroundColor: theme.palette.background.default,
        border: `1px dashed ${theme.palette.divider}`
    },
    count: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.25rem',
        fontWeight: 'bold'
    },
    legendStep: {
        width: 22,
        height: 8,
        borderRadius: 4
    }
}));

// Which cells of the board the contestant took, as a heat map. The busiest cell
// of the board is the top of the ramp, and a cell they never solved is left as
// the surface rather than taking the first step of it.
function GridCard(props) {
    const classes = useStyles();
    const rules = useTableRules();
    const grid = props.grid ?? new Array(9).fill(0);
    const max = Math.max(...grid);
    const min = heatFloor(grid);
    const total = grid.reduce((sum, count) => sum + count, 0);
    return (
        <StatSection
            icon={<GridIcon />}
            title='Grid Distribution'
            contentProps={{ p: 0 }}
        >
            <Box p={2}>
            <div className={classes.board}>
                {grid.map((count, index) => {
                    const step = heatStep(count, max, min);
                    return (
                        <Tooltip
                            key={index}
                            arrow
                            placement='top'
                            title={`${CELL_NAMES[index]}: ${count} solve${count === 1 ? '' : 's'}`}
                        >
                            <div
                                className={`${classes.cell} ${step === -1 ? classes.empty : ''}`}
                                style={step === -1 ? undefined : { backgroundColor: HEAT_STEPS[step] }}
                            >
                                <span
                                    className={classes.count}
                                    style={{ color: step === -1 ? 'inherit' : HEAT_TEXT[step] }}
                                >
                                    {count}
                                </span>
                            </div>
                        </Tooltip>
                    );
                })}
            </div>
            <Box display='flex' alignItems='center' justifyContent='center' mt={2} style={{ gap: 6 }}>
                <Typography variant='caption' color='textSecondary'> fewer </Typography>
                {HEAT_STEPS.map(color => (
                    <div key={color} className={classes.legendStep} style={{ backgroundColor: color }} />
                ))}
                <Typography variant='caption' color='textSecondary'> more </Typography>
            </Box>
            </Box>
            <Table size='small' className={rules.ruled}>
                <TableHead>
                    <TableRow>
                        <TableCell> <Typography variant='body2' noWrap> Cells </Typography> </TableCell>
                        <TableCell align='right'> <Typography variant='body2' noWrap> Solves </Typography> </TableCell>
                        <TableCell align='right'> <Typography variant='body2' noWrap> Share </Typography> </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {CELL_GROUPS.map(group => {
                        const solves = group.cells.reduce((sum, cell) => sum + grid[cell], 0);
                        return (
                            <TableRow key={group.name}>
                                <TableCell>
                                    <Typography variant='body2' noWrap> {group.name} </Typography>
                                </TableCell>
                                <TableCell align='right'> <Typography variant='body2' noWrap> {solves} </Typography> </TableCell>
                                <TableCell align='right'>
                                    <Typography variant='body2' color='textSecondary' noWrap>
                                        {total === 0 ? '—' : `${(solves * 100 / total).toFixed(0)}%`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </StatSection>
    );
}

export default GridCard;
